import { Alert, Anchor, Body, ContentSpinner, Divider, Flex, H3 } from '@taxbit-private/cosmic'
import { useCallback, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { BasePath } from 'routes/paths'

import { isZeusEnvironment } from 'utils/environment'

import NeedsVerification from './NeedsVerification'
import useApi from '../../api/useApi'
import { ResponseError } from '../../api/utils/useFetchWithAuth'
import TaxbitRoundLogo from '../../assets/img/taxbit-round-logo.svg?react'
import { useAuth } from '../../contexts/auth/AuthContext'
import { useLocalStorage } from '../../hooks/useLocalStorage'

function getCleanedRedirectUrl(requestedParams: string, paramName: string) {
  const requested_in = new URLSearchParams(requestedParams).get(paramName)

  return requested_in?.startsWith('/app/') ? decodeURIComponent(requested_in) : undefined
}

const LoginPage = () => {
  const { currentUser, authManager, isLoading: isAuthLoading, renderLoginComponent } = useAuth()

  const [isOrgLoading, setIsOrgLoading] = useState(true)
  const [loginError, setLoginError] = useState<string>()

  const location = useLocation()

  const { fetch_org } = useApi()
  const requestedUrl =
    getCleanedRedirectUrl(useLocation().search, 'requested') || `/app/${BasePath.Dashboard}`

  const [, setOrg] = useLocalStorage('currentOrg')

  const handleFetchOrg = useCallback(async () => {
    setIsOrgLoading(true)
    if (currentUser) {
      try {
        setLoginError(undefined)
        await fetch_org()
      } catch (error) {
        if (error instanceof ResponseError && error.response.status === 401) {
          setLoginError(`We could not find an account for ${currentUser.email}`)
          await authManager?.signOut()
          setOrg(undefined)
        }
      } finally {
        setIsOrgLoading(false)
      }
    }
  }, [currentUser, fetch_org, authManager, setOrg])

  useEffect(() => {
    if (currentUser?.emailVerified) {
      void handleFetchOrg()
    } else {
      setIsOrgLoading(false)
    }
  }, [currentUser, handleFetchOrg])

  if (isAuthLoading || isOrgLoading) {
    return <ContentSpinner />
  }

  if (
    currentUser &&
    currentUser.emailVerified &&
    !loginError &&
    (location.pathname === '/login' || location.pathname === '/login/fox')
  ) {
    return <Navigate to={requestedUrl} replace={true} />
  }

  return (
    <LoginWrapper direction="column" gap="l" alignItems="center">
      <Logo />
      {!currentUser || loginError ? (
        <LoginContents direction="column" gap="l" alignItems="center">
          <H3 as="h1">Log in to Taxbit Accounting Suite</H3>
          {loginError && <Alert variant="danger">{loginError}</Alert>}
          {renderLoginComponent(requestedUrl)}

          {isZeusEnvironment() ? undefined : (
            <>
              <Divider />
              <Flex alignItems="center" gap="s">
                <Body variant="secondary">Interested?</Body>
                <Anchor
                  href="https://taxbit.com/accounting-request-demo/"
                  variant="anchor-primary-inline"
                  label="Schedule a demo"
                  trackingId="login-signup-link"
                />
              </Flex>
            </>
          )}
        </LoginContents>
      ) : (
        <NeedsVerification />
      )}
    </LoginWrapper>
  )
}

const Logo = styled(TaxbitRoundLogo)(
  ({ theme }) => `
  width: ${theme.sizing.xxxl};
  height: ${theme.sizing.xxxl};
`
)

const LoginWrapper = styled(Flex)`
  position: absolute;
  top: 12rem;
  left: 50%;
  transform: translateX(-50%);
`

const LoginContents = styled(Flex)(
  ({ theme }) => `
  width: ${theme.measure.m};
`
)

export default LoginPage
